import React from 'react';
import { useLocation } from 'react-router-dom';
import { Callout, ColorPicker, DefaultButton, DirectionalHint, Dropdown, IconButton, Label, MessageBar, MessageBarType, Panel, PanelType, Pivot, PivotItem, Separator, Stack, Text, TextField, Toggle } from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import { IStorefrontSettings } from '../../../../models/Storefront';
import ImageEditor from '../../../Inputs/ImageEditor';
import FontPicker from '../../../Inputs/FontPicker';
import { BORDER_STYLE_OPTIONS, FONT_WEIGHT_OPTIONS, elementStyleFrom, numFieldStyles, parseNum } from '../styleControls';
import { ISearchElementStyle } from '../searchPageConfig';
import { IHeaderLink, IHeaderLinksConfig, parseHeaderLinksConfig, serializeHeaderLinksConfig, headerLinksJustify } from '../headerLinksConfig';
import { ILayout } from '../layout';
import StorefrontLayoutEditor from '../Layout/StorefrontLayoutEditor';
import LayoutPreview from '../Layout/LayoutPreview';
import Setting, { DynamicSetting } from '../../../Common/Setting';
import ConditionalSetting from '../../../Common/ConditionalSetting';
import { useUrlPanelState } from '../../../../hooks/useUrlState';

// Which Pivot tab a focused style property lives on, for the per-link callout's `?focus=` handling
// in StorefrontHeaderCustomLinksEditor below.
const STYLE_FIELD_PIVOT: { [field: string]: 'font' | 'border' } = {
    font: 'font', color: 'font', fontSize: 'font', fontWeight: 'font',
    backgroundColor: 'border', borderColor: 'border', borderStyle: 'border', borderWidth: 'border', borderRadius: 'border', padding: 'border',
};

interface IProps {
    storefrontSettings: IStorefrontSettings;
    onStorefrontSettingsChange: (newSettings: IStorefrontSettings) => void;
    editMode?: boolean;
}

const newId = () => `hl_${Date.now()}_${Math.floor(Math.random() * 100000)}`;
const hasContent = (l: IHeaderLink) => !!((l.label && l.label.trim()) || l.iconUrl);

// The visual style of a single link, as rendered in the header: the default cell/chip
// style with the per-link overrides layered on top.
const chipCss = (config: IHeaderLinksConfig, link: IHeaderLink): React.CSSProperties => ({
    ...elementStyleFrom(config.cellStyle),
    ...elementStyleFrom(link.style),
});

type StylePatch = Partial<ISearchElementStyle>;
type StyleFieldProps = {
    value?: ISearchElementStyle;
    onChange: (patch: StylePatch) => void;
    /** Names whose style these rows edit; without it they render unmarked and stay unlinkable. */
    settingKeyPrefix?: string;
};

// A single color option that reveals an inline color picker when clicked — no nested popup.
const InlineColorField: React.FC<{ label: string; value?: string; onChange: (v?: string) => void }> = ({ label, value, onChange }) => {
    const [open, setOpen] = React.useState(false);
    return (
        <div className='hdr-inline-color'>
            <div className='hdr-inline-color-row' onClick={() => setOpen(o => !o)} title={`Edit ${label}`}>
                <span className='hdr-inline-color-swatch' style={{ background: value || undefined }} />
                <span className='hdr-inline-color-label'>{label}</span>
                <span className='hdr-inline-color-value'>{value || 'unset'}</span>
                {value && (
                    <IconButton
                        iconProps={{ iconName: 'Clear' }}
                        title={`Clear ${label}`}
                        onClick={(e) => { e.stopPropagation(); onChange(undefined); }}
                        styles={{ root: { width: 24, height: 24 } }}
                    />
                )}
            </div>
            {open && (
                <div style={{ marginTop: 6 }}>
                    <ColorPicker color={value || '#ffffff'} onChange={(_, color) => onChange(color.str)} alphaType='none' />
                </div>
            )}
        </div>
    );
};

// Font family / text color / size / weight — shared by the link and container/cell editors.
const FontStyleFields: React.FC<StyleFieldProps> = ({ value, onChange, settingKeyPrefix }) => {
    const mark = (property: string, control: React.ReactElement) =>
        settingKeyPrefix ? <DynamicSetting key={property} id={`${settingKeyPrefix}.${property}`}>{control}</DynamicSetting> : control;
    return (
    <Stack tokens={{ childrenGap: 8 }}>
        {mark('font', (
        <div>
            <Label styles={{ root: { padding: '0 0 4px' } }}>Font</Label>
            <FontPicker selectedFont={value?.font || ''} onChange={(f) => onChange({ font: f || undefined })} placeholder='Default' />
        </div>
        ))}
        {mark('color', <InlineColorField label='Text color' value={value?.color} onChange={(v) => onChange({ color: v })} />)}
        <Stack horizontal wrap tokens={{ childrenGap: 8 }} verticalAlign='end'>
            {mark('fontSize', <TextField label='Font size %' type='number' min={0} value={value?.fontSize?.toString() ?? ''} onChange={(_, v) => onChange({ fontSize: parseNum(v) })} styles={numFieldStyles} />)}
            {mark('fontWeight', <Dropdown label='Weight' options={FONT_WEIGHT_OPTIONS} selectedKey={value?.fontWeight ?? ''} onChange={(_, o) => onChange({ fontWeight: (o?.key as string) || undefined })} styles={{ root: { width: 140 } }} />)}
        </Stack>
    </Stack>
    );
};

// Background / border / padding — shared by the link and container/cell editors.
const BoxStyleFields: React.FC<StyleFieldProps> = ({ value, onChange, settingKeyPrefix }) => {
    const mark = (property: string, control: React.ReactElement) =>
        settingKeyPrefix ? <DynamicSetting key={property} id={`${settingKeyPrefix}.${property}`}>{control}</DynamicSetting> : control;
    return (
    <Stack tokens={{ childrenGap: 8 }}>
        {mark('backgroundColor', <InlineColorField label='Background' value={value?.backgroundColor} onChange={(v) => onChange({ backgroundColor: v })} />)}
        {mark('borderColor', <InlineColorField label='Border color' value={value?.borderColor} onChange={(v) => onChange({ borderColor: v })} />)}
        <Stack horizontal wrap tokens={{ childrenGap: 8 }} verticalAlign='end'>
            {mark('borderStyle', <Dropdown label='Border' options={BORDER_STYLE_OPTIONS} selectedKey={value?.borderStyle ?? ''} onChange={(_, o) => onChange({ borderStyle: (o?.key as string) || undefined })} styles={{ root: { width: 110 } }} />)}
            {mark('borderWidth', <TextField label='Border W' type='number' min={0} value={value?.borderWidth?.toString() ?? ''} onChange={(_, v) => onChange({ borderWidth: parseNum(v) })} styles={numFieldStyles} />)}
            {mark('borderRadius', <TextField label='Radius' type='number' min={0} value={value?.borderRadius?.toString() ?? ''} onChange={(_, v) => onChange({ borderRadius: parseNum(v) })} styles={numFieldStyles} />)}
            {mark('padding', <TextField label='Padding' type='number' min={0} value={value?.padding?.toString() ?? ''} onChange={(_, v) => onChange({ padding: parseNum(v) })} styles={numFieldStyles} />)}
        </Stack>
    </Stack>
    );
};

// --- Submenu (nested dropdown items) editor — AC-943 / S1.4 ---
// One level of children under a header link (cap 2 levels: children of children aren't offered).
// A link with any submenu item renders as a dropdown in the storefront (see NavBar HeaderDropdown).

const newChildId = () => `hlc_${Date.now()}_${Math.floor(Math.random() * 100000)}`;

const SubmenuEditor: React.FC<{ items: IHeaderLink[]; onChange: (items: IHeaderLink[]) => void; parentLinkId: string | number }> = ({ items, onChange, parentLinkId }) => {
    const update = (i: number, patch: Partial<IHeaderLink>) => onChange(items.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
    const remove = (i: number) => onChange(items.filter((_, idx) => idx !== i));
    const move = (i: number, dir: -1 | 1) => {
        const j = i + dir;
        if (j < 0 || j >= items.length) return;
        const next = [...items];
        [next[i], next[j]] = [next[j], next[i]];
        onChange(next);
    };
    const add = () => onChange([...items, { id: newChildId(), label: 'New item', url: '', newTab: false }]);

    return (
        <Stack tokens={{ childrenGap: 10 }} style={{ paddingTop: 8 }}>
            <span style={{ fontSize: 12, color: '#666' }}>
                Dropdown items shown under this link. Add one or more to turn this link into a menu; leave empty for a plain link.
            </span>
            {items.map((c, i) => (
                <div key={c.id ?? i} style={{ border: '1px solid #edebe9', borderRadius: 6, padding: 8 }}>
                    <Stack horizontal horizontalAlign='space-between' verticalAlign='center'>
                        <Label styles={{ root: { padding: 0 } }}>Item {i + 1}</Label>
                        <Stack horizontal tokens={{ childrenGap: 2 }}>
                            <IconButton iconProps={{ iconName: 'ChevronUp' }} title='Move up' disabled={i === 0} onClick={() => move(i, -1)} styles={{ root: { height: 24 } }} />
                            <IconButton iconProps={{ iconName: 'ChevronDown' }} title='Move down' disabled={i === items.length - 1} onClick={() => move(i, 1)} styles={{ root: { height: 24 } }} />
                            <IconButton iconProps={{ iconName: 'Delete' }} title='Delete item' onClick={() => remove(i)} styles={{ root: { height: 24 } }} />
                        </Stack>
                    </Stack>
                    <DynamicSetting id={`storefront.headerLink.${parentLinkId}.child.${c.id ?? i}.label`}>
                        <TextField label='Label' value={c.label ?? ''} onChange={(_, v) => update(i, { label: v ?? '' })} />
                    </DynamicSetting>
                    <DynamicSetting id={`storefront.headerLink.${parentLinkId}.child.${c.id ?? i}.url`}>
                        <TextField label='URL' placeholder='/products or https://…' value={c.url ?? ''} onChange={(_, v) => update(i, { url: v ?? '' })} />
                    </DynamicSetting>
                    <DynamicSetting id={`storefront.headerLink.${parentLinkId}.child.${c.id ?? i}.newTab`}>
                        <Toggle label='Open in new tab' inlineLabel checked={!!c.newTab} onChange={(_, ch) => update(i, { newTab: !!ch })} />
                    </DynamicSetting>
                </div>
            ))}
            <button type='button' className='storefront-header-add-link-btn' onClick={add} title='Add a submenu item'>
                + Add submenu item
            </button>
        </Stack>
    );
};

// --- A single editable link chip + its per-link tabbed callout ---

interface IChipProps {
    link: IHeaderLink;
    index: number;
    count: number;
    css: React.CSSProperties;
    storefrontId: string;
    settings: IStorefrontSettings;
    autoOpen: boolean;
    /** Which Pivot tab to land on when autoOpen is true because a `?focus=` named this link. */
    focusedPivot?: 'link' | 'font' | 'border' | 'submenu';
    onChange: (index: number, patch: Partial<IHeaderLink>) => void;
    onStyleChange: (index: number, patch: StylePatch) => void;
    onDelete: (index: number) => void;
    onMove: (index: number, dir: -1 | 1) => void;
    onDismiss: () => void;
}

const HeaderLinkChip: React.FC<IChipProps> = (p) => {
    const [open, setOpen] = React.useState(p.autoOpen);
    const [megaOpen, setMegaOpen] = React.useState(false);
    const btnId = useId('hdr-link-chip');
    const { link, index, settings } = p;
    const style = link.style ?? {};
    const setStyle = (patch: StylePatch) => p.onStyleChange(index, patch);
    const hasPanel = !!link.panel && (link.panel.rows?.length ?? 0) > 0;

    return (
        <>
            <button id={btnId} type='button' className='storefront-header-link-chip' style={p.css} onClick={() => setOpen(true)} title='Click to edit this link'>
                {link.iconUrl && <img src={link.iconUrl} alt='' style={{ height: '1.2em', width: 'auto', objectFit: 'contain' }} />}
                <span>{link.label?.trim() || '(untitled)'}</span>
            </button>
            {open && (
                <Callout
                    target={`#${btnId}`}
                    onDismiss={() => { setOpen(false); p.onDismiss(); }}
                    setInitialFocus
                    directionalHint={DirectionalHint.bottomCenter}
                    style={{ padding: '1em', width: 340 }}
                >
                    <Stack tokens={{ childrenGap: 8 }}>
                        <Stack horizontal horizontalAlign='space-between' verticalAlign='center'>
                            <Label styles={{ root: { padding: 0 } }}>Link {index + 1}</Label>
                            <Stack horizontal tokens={{ childrenGap: 4 }}>
                                <IconButton iconProps={{ iconName: 'ChevronLeft' }} title='Move left' disabled={index === 0} onClick={() => p.onMove(index, -1)} />
                                <IconButton iconProps={{ iconName: 'ChevronRight' }} title='Move right' disabled={index === p.count - 1} onClick={() => p.onMove(index, 1)} />
                                <IconButton iconProps={{ iconName: 'Delete' }} title='Delete link' onClick={() => { p.onDelete(index); setOpen(false); }} />
                            </Stack>
                        </Stack>

                        {/* defaultSelectedKey lands on the tab a `?focus=` named (e.g. a style property
                            opens Font/Border & Padding, a submenu child opens Submenu); Pivot falls
                            back to the first tab when it's unset. */}
                        <Pivot defaultSelectedKey={p.autoOpen ? (p.focusedPivot ?? 'link') : undefined}>
                            <PivotItem itemKey='link' headerText='Link'>
                                <Stack tokens={{ childrenGap: 8 }} style={{ paddingTop: 8 }}>
                                    <DynamicSetting id={`storefront.headerLink.${link.id ?? index}.label`}>
                                        <TextField label='Label' value={link.label ?? ''} onChange={(_, v) => p.onChange(index, { label: v ?? '' })} />
                                    </DynamicSetting>
                                    <DynamicSetting id={`storefront.headerLink.${link.id ?? index}.url`}>
                                        <TextField label='URL' placeholder='/products or https://…' value={link.url ?? ''} onChange={(_, v) => p.onChange(index, { url: v ?? '' })} />
                                    </DynamicSetting>
                                    <DynamicSetting id={`storefront.headerLink.${link.id ?? index}.newTab`}>
                                        <Toggle label='Open in new tab' inlineLabel checked={!!link.newTab} onChange={(_, checked) => p.onChange(index, { newTab: !!checked })} />
                                    </DynamicSetting>
                                    <DynamicSetting id={`storefront.headerLink.${link.id ?? index}.iconUrl`}>
                                        <ImageEditor
                                            label='Icon (shown left of the label)'
                                            value={link.iconUrl}
                                            onChange={(v) => p.onChange(index, { iconUrl: v })}
                                            onClear={() => p.onChange(index, { iconUrl: '' })}
                                            height={40}
                                            width={40}
                                            imageId={`${p.storefrontId}-${link.id ?? index}`}
                                            imageType='Storefront'
                                        />
                                    </DynamicSetting>
                                </Stack>
                            </PivotItem>

                            <PivotItem itemKey='font' headerText='Font'>
                                <div style={{ paddingTop: 8 }}>
                                    <FontStyleFields value={style} onChange={setStyle} settingKeyPrefix={`storefront.headerLink.${link.id ?? index}.style`} />
                                </div>
                            </PivotItem>

                            <PivotItem itemKey='border' headerText='Border & Padding'>
                                <div style={{ paddingTop: 8 }}>
                                    <BoxStyleFields value={style} onChange={setStyle} settingKeyPrefix={`storefront.headerLink.${link.id ?? index}.style`} />
                                </div>
                            </PivotItem>

                            <PivotItem itemKey='submenu' headerText='Submenu'>
                                <SubmenuEditor
                                    items={link.children ?? []}
                                    onChange={(items) => p.onChange(index, { children: items.length ? items : undefined })}
                                    parentLinkId={link.id ?? index}
                                />
                            </PivotItem>

                            <PivotItem itemKey='mega' headerText='Mega-menu'>
                                <Stack tokens={{ childrenGap: 8 }} style={{ paddingTop: 8 }}>
                                    <Text variant='small' styles={{ root: { color: '#605e5c' } }}>
                                        A mega-menu shows a rich grid panel on hover. When set, it supersedes the Submenu on desktop.
                                    </Text>
                                    {hasPanel && (
                                        <LayoutPreview layout={link.panel as ILayout} region='header' logo={settings.headerLogo} title={settings.title || settings.name} barColor='#ffffff' barTextColor='#111111' />
                                    )}
                                    <ConditionalSetting when={hasPanel} idPrefix={`storefront.headerLink.${link.id ?? index}.panelFullWidth`}>
                                        <DynamicSetting id={`storefront.headerLink.${link.id ?? index}.panelFullWidth`}>
                                            <Toggle
                                                label='Full-width bar (spans the header, drops below it)'
                                                inlineLabel
                                                checked={!!link.panelFullWidth}
                                                onChange={(_, c) => p.onChange(index, { panelFullWidth: c || undefined })}
                                                styles={{ root: { marginBottom: 0 }, label: { fontSize: 12 } }}
                                            />
                                        </DynamicSetting>
                                    </ConditionalSetting>
                                    {hasPanel ? (
                                        <Stack horizontal tokens={{ childrenGap: 8 }}>
                                            <DefaultButton text='Edit panel…' iconProps={{ iconName: 'GridViewMedium' }} onClick={() => setMegaOpen(true)} />
                                            <DefaultButton text='Remove panel' iconProps={{ iconName: 'Delete' }} onClick={() => p.onChange(index, { panel: undefined })} />
                                        </Stack>
                                    ) : (
                                        <DefaultButton
                                            text='Add mega-menu panel'
                                            iconProps={{ iconName: 'Add' }}
                                            onClick={() => { p.onChange(index, { panel: { rows: [{ columns: [{ elements: [] }] }] } }); setMegaOpen(true); }}
                                        />
                                    )}
                                </Stack>
                            </PivotItem>
                        </Pivot>
                    </Stack>
                </Callout>
            )}

            <Panel
                isOpen={megaOpen}
                onDismiss={() => setMegaOpen(false)}
                headerText={`Mega-menu: ${link.label?.trim() || 'link'}`}
                type={PanelType.large}
                isLightDismiss
                closeButtonAriaLabel='Close'
            >
                <Stack tokens={{ childrenGap: 12 }}>
                    <MessageBar messageBarType={MessageBarType.info}>
                        This panel appears on hover of “{link.label?.trim() || 'this link'}”, superseding its submenu on desktop. Remove every row to drop back to the submenu / plain link.
                    </MessageBar>
                    {hasPanel && (
                        <div>
                            <div style={{ fontSize: 11, color: '#605e5c', marginBottom: 4 }}>Live preview (approximate)</div>
                            <LayoutPreview layout={link.panel as ILayout} region='header' logo={settings.headerLogo} title={settings.title || settings.name} barColor='#ffffff' barTextColor='#111111' />
                        </div>
                    )}
                    <StorefrontLayoutEditor layout={link.panel} onChange={(panel) => p.onChange(index, { panel })} />
                </Stack>
            </Panel>
        </>
    );
};

// --- Main editor: dashed container in the header center, chips, Add Link, container/cell panel ---

const StorefrontHeaderCustomLinksEditor: React.FC<IProps> = (props) => {
    const config = React.useMemo(() => parseHeaderLinksConfig(props.storefrontSettings.headerLinksConfig), [props.storefrontSettings.headerLinksConfig]);
    const links = config.links ?? [];
    /*
     * The gear-icon panel (align / container / cell styling) used to be a plain useState Panel, the
     * same "toolbar-opened editor" bug the home page editor's Nav Pods / Affiliations panels had — a
     * link to storefront.headerLinks.align or a container/cell style id landed on this tab with the
     * panel shut and nothing to find. ?panel=headerLinks (area storefront.pages.headerLinks) opens it.
     */
    const panelLink = useUrlPanelState('headerLinks');
    const panelOpen = panelLink.isOpen;
    const [justAddedId, setJustAddedId] = React.useState<string | null>(null);

    /*
     * The per-link fields (storefront.headerLink.<id>.*, including .style.* and .child.<cid>.*) live
     * behind a DIFFERENT gate: each link's own callout, opened by clicking that chip. That's "an
     * instance nothing has selected" (see settings/README.md), so the fix is the same one the banner
     * editor uses for slides — read the linked id out of `?focus=` and have the matching chip select
     * itself, rather than routing these through the gear panel (which doesn't hold them).
     */
    const location = useLocation();
    const focus = new URLSearchParams(location.search).get('focus') ?? '';
    const focusedLinkId = /^storefront\.headerLink\.([^.]+)\./.exec(focus)?.[1];
    const focusedPivot: 'link' | 'font' | 'border' | 'submenu' = focus.includes('.child.')
        ? 'submenu'
        : (() => {
            const styleField = /\.style\.([A-Za-z]+)$/.exec(focus)?.[1];
            return styleField ? (STYLE_FIELD_PIVOT[styleField] ?? 'font') : 'link';
        })();

    const commit = (next: IHeaderLinksConfig) => {
        props.onStorefrontSettingsChange({ ...props.storefrontSettings, headerLinksConfig: serializeHeaderLinksConfig(next) });
    };
    const updateLink = (i: number, patch: Partial<IHeaderLink>) =>
        commit({ ...config, links: links.map((l, idx) => (idx === i ? { ...l, ...patch } : l)) });
    const updateLinkStyle = (i: number, patch: StylePatch) =>
        commit({ ...config, links: links.map((l, idx) => (idx === i ? { ...l, style: { ...(l.style ?? {}), ...patch } } : l)) });
    const updateAlign = (align: 'left' | 'center' | 'right') => commit({ ...config, align });
    const updateContainer = (patch: StylePatch) =>
        commit({ ...config, container: { ...(config.container ?? {}), ...patch } });
    const updateCell = (patch: StylePatch) =>
        commit({ ...config, cellStyle: { ...(config.cellStyle ?? {}), ...patch } });
    const removeLink = (i: number) =>
        commit({ ...config, links: links.filter((_, idx) => idx !== i) });
    const move = (i: number, dir: -1 | 1) => {
        const j = i + dir;
        if (j < 0 || j >= links.length) return;
        const next = [...links];
        [next[i], next[j]] = [next[j], next[i]];
        commit({ ...config, links: next });
    };
    const addLink = () => {
        const id = newId();
        commit({ ...config, links: [...links, { id, label: 'New Link', url: '', newTab: false }] });
        setJustAddedId(id);
    };

    const hasLinks = links.some(hasContent);

    // Outside edit mode, render the links exactly as the storefront will (nothing when empty).
    if (!props.editMode) {
        if (!hasLinks) return null;
        return (
            <div className='storefront-header-links-slot'>
                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '1.5em', ...elementStyleFrom(config.container), justifyContent: headerLinksJustify(config.align) }}>
                    {links.filter(hasContent).map((l, i) => (
                        <span key={l.id ?? i} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4em', ...chipCss(config, l) }}>
                            {l.iconUrl && <img src={l.iconUrl} alt='' style={{ height: '1.2em', width: 'auto', objectFit: 'contain' }} />}
                            {l.label && <span>{l.label}</span>}
                        </span>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className='storefront-header-links-slot'>
            <div className='storefront-header-links-edit storefront-editable-area' style={{ ...elementStyleFrom(config.container), justifyContent: headerLinksJustify(config.align) }}>
                {links.map((link, i) => (
                    <HeaderLinkChip
                        key={link.id ?? i}
                        link={link}
                        index={i}
                        count={links.length}
                        css={chipCss(config, link)}
                        storefrontId={props.storefrontSettings.id ?? ''}
                        settings={props.storefrontSettings}
                        autoOpen={(!!link.id && link.id === justAddedId) || String(link.id ?? i) === focusedLinkId}
                        focusedPivot={focusedPivot}
                        onChange={updateLink}
                        onStyleChange={updateLinkStyle}
                        onDelete={removeLink}
                        onMove={move}
                        onDismiss={() => setJustAddedId(null)}
                    />
                ))}
                <button type='button' className='storefront-header-add-link-btn' onClick={addLink} title='Add a header link'>
                    + Add Link
                </button>
                <IconButton
                    className='hdr-links-gear'
                    iconProps={{ iconName: 'Edit' }}
                    title='Edit links container & cell style'
                    ariaLabel='Edit links container & cell style'
                    onClick={() => panelLink.open()}
                />
            </div>

            <Panel
                isOpen={panelOpen}
                onDismiss={() => panelLink.close()}
                headerText='Header Links Style'
                type={PanelType.medium}
                isLightDismiss
                closeButtonAriaLabel='Close'
            >
                <Stack tokens={{ childrenGap: 12 }}>
                    <span style={{ fontSize: 12, color: '#666' }}>
                        Styling for the whole links container and the default look of every link cell/chip.
                        Individual links can override this from their own editor.
                    </span>
                    <Separator>Layout</Separator>
                    <Setting id='storefront.headerLinks.align'>
                        <Dropdown
                            label='Alignment'
                            selectedKey={config.align ?? 'center'}
                            onChange={(_, o) => updateAlign((o?.key as 'left' | 'center' | 'right') || 'center')}
                            options={[
                                { key: 'left', text: 'Left (next to the logo)' },
                                { key: 'center', text: 'Center' },
                                { key: 'right', text: 'Right (next to the header actions)' },
                            ]}
                            styles={{ root: { maxWidth: 320 } }}
                        />
                    </Setting>
                    <Separator>Container</Separator>
                    <FontStyleFields value={config.container} onChange={updateContainer} settingKeyPrefix='storefront.headerLinks.container' />
                    <BoxStyleFields value={config.container} onChange={updateContainer} settingKeyPrefix='storefront.headerLinks.container' />
                    <Separator>Cells / Chips</Separator>
                    <FontStyleFields value={config.cellStyle} onChange={updateCell} settingKeyPrefix='storefront.headerLinks.cell' />
                    <BoxStyleFields value={config.cellStyle} onChange={updateCell} settingKeyPrefix='storefront.headerLinks.cell' />
                </Stack>
            </Panel>
        </div>
    );
};

export default StorefrontHeaderCustomLinksEditor;
